import type { Cancellable } from '../../../types';
import type { ReactElement, ReactNode } from 'react';

import { useStackModal } from '@rango-dev/internal-profile/hooks';
import { Button, Flex, Skeleton, Text } from '@rango-dev/whale';
import { useWidget } from '@rango-dev/widget-embedded';
import { ChevronRight, CircleUser, RotateCw, TriangleAlert } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearAuthError, fetchProfileSummary } from '../../../state/ProfileSlice';
import { Avatar } from '../../Profile/Avatar';
import { shortenUsername } from '../../Profile/Leaderboard/Leaderboard.helpers';
import { displayUserRank } from '../../Profile/ProfileSummary/ProfileSummary.helpers';

import styles from './WalletDetailsProfile.module.css';

const USER_ICON_SIZE = 20;
const CHEVRON_ICON_SIZE = 16;
const ALERT_ICON_SIZE = 16;
const ALERT_ICON_CONTAINER_HEIGHT = '20px';
const USERNAME_START_CHARS = 8;
const USERNAME_END_CHARS = 6;
const AVATAR_SKELETON_SIZE = '32px';
const TEXT_SKELETON_HEIGHT = '20px';
const USERNAME_SKELETON_WIDTH = '128px';
const SCORE_SKELETON_WIDTH = '96px';

function ProfileCard(props: { children: ReactNode }): ReactElement {
  return (
    <Flex
      direction="column"
      align="start"
      p="3"
      width="100%"
      overflow="hidden"
      className={styles.profileCard}>
      {props.children}
    </Flex>
  );
}

type ProfileErrorCardProps = {
  buttonId: string;
  message: string;
  onRetry: () => void;
};

/* Red row shared by the failed sign in and the failed profile fetch. It has no border. */
function ProfileErrorCard(props: ProfileErrorCardProps): ReactElement {
  const { buttonId, message, onRetry } = props;
  const { t } = useTranslation();

  return (
    <Flex direction="column" align="start" p="3" width="100%" className={styles.errorCard}>
      <Flex align="center" justify="end" gap="4" width="100%" wrap="wrap">
        <Flex align="center" gap="2" flexGrow="1">
          <Flex align="center" flexShrink="0" height={ALERT_ICON_CONTAINER_HEIGHT}>
            <TriangleAlert size={ALERT_ICON_SIZE} className={styles.errorIcon} />
          </Flex>
          <Text size="2" className={styles.errorText}>
            {message}
          </Text>
        </Flex>
        <Button id={buttonId} size="2" color="red" radius="full" onClick={onRetry}>
          <RotateCw size={ALERT_ICON_SIZE} />
          {t('Retry')}
        </Button>
      </Flex>
    </Flex>
  );
}

export function WalletDetailsProfile(): ReactElement | null {
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const username = useAppSelector((state) => state.profile.username);
  const profileSummary = useAppSelector((state) => state.profile.profileSummary.private);
  const profileAuth = useAppSelector((state) => state.profile.profileAuth);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clear } = useStackModal();
  const {
    wallets: { details },
  } = useWidget();
  const fetchProfileSummaryPromise = useRef<Cancellable | null>(null);

  const retryFetchProfileSummary = () => {
    if (username) {
      fetchProfileSummaryPromise.current = dispatch(
        fetchProfileSummary({ username, public: false }),
      );
    }
  };

  const goToProfile = () => navigate('../profile');

  const retrySignIn = () => {
    dispatch(clearAuthError());
    clear();
    goToProfile();
  };

  useEffect(() => {
    return () => fetchProfileSummaryPromise.current?.abort();
  }, []);

  const viewProfile = () => {
    goToProfile();
    clear();
  };

  // The profile section is only shown while at least one wallet is connected.
  if (!details.length) {
    return null;
  }

  if (!isLoggedIn && profileAuth.loading) {
    return (
      <ProfileCard>
        <Flex align="center" justify="end" gap="2" width="100%" wrap="wrap">
          <Flex align="center" gap="2" flexGrow="1">
            <CircleUser size={USER_ICON_SIZE} className={styles.icon} />
            <Text size="2" weight="medium" className={styles.description}>
              {t('Signing you in…')}
            </Text>
          </Flex>
          <Button
            id="wallet-details-modal-signing-in-btn"
            size="1"
            variant="ghost"
            radius="full"
            className={styles.signInButton}
            loading>
            {t('Sign in')}
            <ChevronRight size={CHEVRON_ICON_SIZE} />
          </Button>
        </Flex>
      </ProfileCard>
    );
  }

  if (!isLoggedIn && profileAuth.error) {
    return (
      <ProfileErrorCard
        buttonId="wallet-details-modal-retry-sign-in-btn"
        message={t('Login failed, please retry.')}
        onRetry={retrySignIn}
      />
    );
  }

  if (!isLoggedIn || !username) {
    return (
      <ProfileCard>
        <Flex align="center" justify="end" gap="2" width="100%" wrap="wrap">
          <Flex align="center" gap="2" flexGrow="1">
            <CircleUser size={USER_ICON_SIZE} className={styles.icon} />
            <Text size="2" weight="medium" className={styles.description}>
              {t('Get insights into your swaps and rank')}
            </Text>
          </Flex>
          <Button
            id="wallet-details-modal-sign-in-btn"
            size="1"
            variant="ghost"
            radius="full"
            className={styles.signInButton}
            onClick={() => {
              clear();
              goToProfile();
            }}>
            {t('Sign in')}
            <ChevronRight size={CHEVRON_ICON_SIZE} />
          </Button>
        </Flex>
      </ProfileCard>
    );
  }

  if (profileSummary.data) {
    const { level, score, username: profileUsername } = profileSummary.data;

    return (
      <ProfileCard>
        <Flex align="center" gap="2" width="100%">
          <Flex align="center" gap="3" flexGrow="1" minWidth="0" overflow="hidden">
            <Avatar size="2" username={profileUsername} className={styles.avatar} />
            <Flex direction="column" align="start" gap="1" minWidth="0" overflow="hidden">
              <Text size="2" weight="medium" truncate>
                {shortenUsername({
                  username: profileUsername,
                  startChars: USERNAME_START_CHARS,
                  endChars: USERNAME_END_CHARS,
                })}
              </Text>
              <Text size="2" color="gray" truncate>
                {t('Lv {{level}} · {{score}} Score', {
                  level: level.level,
                  score: displayUserRank(score),
                })}
              </Text>
            </Flex>
          </Flex>
          <Button
            id="wallet-details-modal-view-profile-btn"
            size="1"
            variant="ghost"
            radius="full"
            className={styles.signInButton}
            onClick={viewProfile}>
            {t('View profile')}
            <ChevronRight size={CHEVRON_ICON_SIZE} />
          </Button>
        </Flex>
      </ProfileCard>
    );
  }

  if (profileSummary.loading) {
    return (
      <ProfileCard>
        <Flex align="center" gap="3" width="100%">
          <Skeleton
            width={AVATAR_SKELETON_SIZE}
            height={AVATAR_SKELETON_SIZE}
            className={styles.avatarSkeleton}
          />
          <Flex direction="column" align="start" gap="1">
            <Skeleton width={USERNAME_SKELETON_WIDTH} height={TEXT_SKELETON_HEIGHT} />
            <Skeleton width={SCORE_SKELETON_WIDTH} height={TEXT_SKELETON_HEIGHT} />
          </Flex>
        </Flex>
      </ProfileCard>
    );
  }

  if (profileSummary.error) {
    return (
      <ProfileErrorCard
        buttonId="wallet-details-modal-retry-profile-btn"
        message={t('An unexpected error occurred, please try again')}
        onRetry={retryFetchProfileSummary}
      />
    );
  }

  return null;
}
